import React, { createContext, useContext, useState } from 'react';
import { DoctorLetterForm, PharmacistLetterForm } from '../types';
import { useForm } from 'react-hook-form';
import { LetterType } from '../PharmacistDoctorLetterSection';
import { useToast } from '../../../../../../context';
import { post } from '../../../../../../api/baseUrl';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../../../store';
import type { MedProfile } from '../../../hook/types';
import { getDefaultIntroductions } from '../utils';
import useLatestEpisode from '../../../../../hooks/useLatestEpisode';
import { usePdfUtils } from '../../../hook';

interface LettersFormContextProps {
  isDownloadingDoctor: boolean;
  isDownloadingPharmacist: boolean;
  doctorLetterForm: ReturnType<typeof useForm<DoctorLetterForm>>;
  pharmacistLetterForm: ReturnType<typeof useForm<PharmacistLetterForm>>;
  onDownloadLetter: (type: LetterType) => void;
  onGenerateMedicationChanges: (type: LetterType) => void;
  commitDoctorLetterState: () => void;
  commitPharmacistLetterState: () => void;
  resetDoctorLetterToCommittedState: () => void;
  resetPharmacistLetterToCommittedState: () => void;
}
const LettersFormContext = createContext<LettersFormContextProps>({
  isDownloadingDoctor: false,
  isDownloadingPharmacist: false,
  doctorLetterForm: {} as ReturnType<typeof useForm<DoctorLetterForm>>,
  onDownloadLetter: () => {},
  onGenerateMedicationChanges: () => {},
  commitDoctorLetterState: () => {},
  commitPharmacistLetterState: () => {},
  resetDoctorLetterToCommittedState: () => {},
  resetPharmacistLetterToCommittedState: () => {},
  pharmacistLetterForm: {} as ReturnType<typeof useForm<PharmacistLetterForm>>,
});

export const useLettersForm = () => useContext(LettersFormContext);

const LettersFormProvider = ({
  children,
  medProfile,
  onProvideValues,
}: {
  children: React.ReactNode;
  medProfile: MedProfile;
  onProvideValues?: (
    getter: () => {
      doctorLetter: DoctorLetterForm;
      pharmacistLetter: PharmacistLetterForm;
    },
  ) => void;
}) => {
  const { setToast } = useToast();
  const { downloadPdfFromBase64 } = usePdfUtils();
  const [searchParams] = useSearchParams();
  const { latestEpisode, open: latestEpisodeOpen } = useLatestEpisode();
  const { patientProfile } = useSelector(
    (state: RootState) => state.patientProfile,
  );
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const patientAccessToken = searchParams.get('access_token') || '';
  const buildDoctorLetter = React.useCallback(
    (): DoctorLetterForm => ({
      gpName: medProfile.doctorLetter?.gpName || '',
      gpDetails: medProfile.doctorLetter?.gpDetails || '',
      introduction:
        medProfile.doctorLetter?.introduction ||
        getDefaultIntroductions(
          `${patientProfile.first_name} ${patientProfile.last_name}`,
          latestEpisodeOpen ? undefined : latestEpisode?.episode_closed_date,
        ),
      summaryOfRecommendations: medProfile.doctorLetter
        ?.summaryOfRecommendations || [''],
      monitoringRequired: medProfile.doctorLetter?.monitoringRequired || [''],
      medicationChanges: medProfile.doctorLetter?.medicationChanges || '',
    }),
    [
      medProfile.doctorLetter,
      patientProfile.first_name,
      patientProfile.last_name,
      latestEpisodeOpen,
      latestEpisode?.episode_closed_date,
    ],
  );
  const buildPharmacistLetter = React.useCallback(
    (): PharmacistLetterForm => ({
      pharmacistName: medProfile.pharmacistLetter?.pharmacistName || '',
      introduction:
        medProfile.pharmacistLetter?.introduction ||
        getDefaultIntroductions(
          `${patientProfile.first_name} ${patientProfile.last_name}`,
          latestEpisodeOpen ? undefined : latestEpisode?.episode_closed_date,
        ),
      summaryOfRecommendations: medProfile.pharmacistLetter
        ?.summaryOfRecommendations || [''],
      medicationChanges: medProfile.pharmacistLetter?.medicationChanges || '',
    }),
    [
      medProfile.pharmacistLetter,
      patientProfile.first_name,
      patientProfile.last_name,
      latestEpisodeOpen,
      latestEpisode?.episode_closed_date,
    ],
  );
  const doctorLetter = React.useMemo(
    () => buildDoctorLetter(),
    [buildDoctorLetter],
  );
  const pharmacistLetter = React.useMemo(
    () => buildPharmacistLetter(),
    [buildPharmacistLetter],
  );
  const [isDownloadingPharmacist, setIsDownloadingPharmacist] = useState(false);
  const [isDownloadingDoctor, setIsDownloadingDoctor] = useState(false);
  const [committedDoctorLetter, setCommittedDoctorLetter] =
    useState<DoctorLetterForm>(doctorLetter);
  const [committedPharmacistLetter, setCommittedPharmacistLetter] =
    useState<PharmacistLetterForm>(pharmacistLetter);

  const doctorLetterForm = useForm<DoctorLetterForm>({
    defaultValues: doctorLetter,
  });
  const pharmacistLetterForm = useForm<PharmacistLetterForm>({
    defaultValues: pharmacistLetter,
  });

  React.useEffect(() => {
    setCommittedDoctorLetter(doctorLetter);
    doctorLetterForm.reset(doctorLetter);
  }, [doctorLetter, doctorLetterForm]);

  React.useEffect(() => {
    setCommittedPharmacistLetter(pharmacistLetter);
    pharmacistLetterForm.reset(pharmacistLetter);
  }, [pharmacistLetter, pharmacistLetterForm]);

  const commitDoctorLetterState = React.useCallback(() => {
    const values = doctorLetterForm.getValues();
    setCommittedDoctorLetter(values);
    doctorLetterForm.reset(values);
  }, [doctorLetterForm]);

  const commitPharmacistLetterState = React.useCallback(() => {
    const values = pharmacistLetterForm.getValues();
    setCommittedPharmacistLetter(values);
    pharmacistLetterForm.reset(values);
  }, [pharmacistLetterForm]);

  const resetDoctorLetterToCommittedState = React.useCallback(() => {
    doctorLetterForm.reset(committedDoctorLetter);
  }, [committedDoctorLetter, doctorLetterForm]);

  const resetPharmacistLetterToCommittedState = React.useCallback(() => {
    pharmacistLetterForm.reset(committedPharmacistLetter);
  }, [committedPharmacistLetter, pharmacistLetterForm]);

  // Expose a stable getter so parent can pull current values on demand
  React.useEffect(() => {
    if (onProvideValues) {
      onProvideValues(() => ({
        doctorLetter: doctorLetterForm.getValues(),
        pharmacistLetter: pharmacistLetterForm.getValues(),
      }));
    }
  }, [onProvideValues, doctorLetterForm, pharmacistLetterForm]);

  const onDownloadLetter = async (type: LetterType) => {
    if (!patientAccessToken || !selectedTenancy) {
      setToast('Failed to download letter', {
        autohide: true,
        animation: true,
      });
      return;
    }

    const trimedPharmacistLetter = {
      ...pharmacistLetterForm.getValues(),
      summaryOfRecommendations:
        pharmacistLetterForm
          .getValues()
          .summaryOfRecommendations?.filter(Boolean) || [],
    };

    const trimedDoctorLetter = {
      ...doctorLetterForm.getValues(),
      monitoringRequired: doctorLetterForm
        .getValues()
        .monitoringRequired?.filter(Boolean),
      summaryOfRecommendations:
        doctorLetterForm
          .getValues()
          .summaryOfRecommendations?.filter(Boolean) || [],
    };

    const payload = {
      access_token: patientAccessToken,
      tenancyId: selectedTenancy.tenancy_id,
      pharmacistLetter: trimedPharmacistLetter,
      doctorLetter: trimedDoctorLetter,
      letterType: type,
      medicinProfileId: medProfile?.id,
    };
    try {
      if (type == LetterType.PHARMACIST) {
        setIsDownloadingPharmacist(true);
      } else {
        setIsDownloadingDoctor(true);
      }
      const res = await post('getPharmacistDoctorLetter', payload);
      if (res.data.message === 'Success') {
        setToast('Letter downloaded successfully', {
          autohide: true,
          animation: true,
        });
        downloadPdfFromBase64(res.data.data.body);
      }
    } catch (error) {
      setToast('Failed to download letter', {
        autohide: true,
        animation: true,
      });
    } finally {
      if (type == LetterType.PHARMACIST) {
        setIsDownloadingPharmacist(false);
      } else {
        setIsDownloadingDoctor(false);
      }
    }
  };

  const onGenerateMedicationChanges = (type: LetterType) => {
    const allMeds = medProfile.medSections.flatMap(
      (section) => section.medications,
    );

    if (!allMeds.length) {
      setToast('No medication changes this admission available.', {
        autohide: true,
        animation: true,
      });
      return;
    }
    // Collect unique change types in encounter order
    const uniqueChangeTypes = Array.from(
      new Set(
        allMeds
          .map((med) => (med.medChangeType ? med.medChangeType : null))
          .filter(Boolean),
      ),
    ) as string[];

    // Group medications by change type and build a readable block per type
    const blocks = uniqueChangeTypes
      .map((changeType) => {
        if (changeType != 'No Changes') {
          const medsForType = allMeds.filter(
            (med) => med.medChangeType === changeType,
          );
          const medLines = medsForType
            .filter((med) => med.medName)
            .map((med) => {
              const desc = med.furtherDescriptionOfChange
                ? ` (${med.furtherDescriptionOfChange})`
                : '';
              return `• ${med.medName}${desc}`;
            });
          if (!medLines.length) return '';
          return `${changeType}\n${medLines.join('\n')}`;
        }
      })
      .filter(Boolean);
    const allMedicationChanges = blocks.join('\n\n');

    if (allMedicationChanges.length == 0) {
      setToast('No medication changes this admission available.', {
        autohide: true,
        animation: true,
      });
      return;
    }

    if (type === LetterType.PHARMACIST) {
      pharmacistLetterForm.setValue('medicationChanges', allMedicationChanges, {
        shouldDirty: true,
      });
    } else {
      doctorLetterForm.setValue('medicationChanges', allMedicationChanges, {
        shouldDirty: true,
      });
    }
  };

  return (
    <LettersFormContext.Provider
      value={{
        isDownloadingDoctor,
        isDownloadingPharmacist,
        doctorLetterForm,
        pharmacistLetterForm,
        onDownloadLetter,
        onGenerateMedicationChanges,
        commitDoctorLetterState,
        commitPharmacistLetterState,
        resetDoctorLetterToCommittedState,
        resetPharmacistLetterToCommittedState,
      }}
    >
      {children}
    </LettersFormContext.Provider>
  );
};

export default LettersFormProvider;
